import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HealthResponse, JobRun, Page } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import { AgeText } from '@/components/Provenance';
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Pill,
  Skeleton,
  useToast,
  type Column,
} from '@/components';
import { useSession } from '@/app/session';

interface SystemResponse {
  version: string;
  build: string;
  uptime_sec: number;
  db: string;
  wp: string;
  wc: string;
  bridge: string;
  jobs: string;
}

/**
 * Stamped by hand when the bundle ships. It is the one string on this page that proves
 * which web build the browser actually loaded, which is the difference between "deployed"
 * and "deployed and reached the user". infra/scripts/deploy.sh web is what ships it.
 */
const WEB_BUILD = '2026-09-06.1';

interface JobRow {
  name: string;
  description: string;
  enabled: boolean;
  last_run_at: string | null;
  last_ok_at: string | null;
  next_run_at: string | null;
}

/**
 * Settings > System. The detail behind every chip in the Home health strip, plus the job
 * table and the run-now button.
 *
 * Running a job now is tier 2: it is not destructive, but it can hammer WooCommerce, so it
 * takes a step-up and leaves an audit row.
 */
export function System() {
  const client = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const [runNow, setRunNow] = useState<JobRow | null>(null);
  const [openRuns, setOpenRuns] = useState<string | null>(null);

  const system = useQuery({
    queryKey: qk.settings('system'),
    queryFn: () => api.get<SystemResponse>('/settings/system'),
  });

  const health = useQuery({
    queryKey: qk.healthDetail(),
    queryFn: () => api.get<HealthResponse>('/health/detail'),
    refetchInterval: 60_000,
  });

  const jobs = useQuery({
    queryKey: qk.jobs(),
    queryFn: () => api.get<Page<JobRow>>('/jobs', { query: { limit: 100 } }),
    enabled: can('jobs.read'),
  });

  const runs = useQuery({
    queryKey: qk.jobRuns(openRuns ?? 'none'),
    queryFn: () => api.get<Page<JobRun>>(`/jobs/${openRuns ?? ''}/runs`, { query: { limit: 20 } }),
    enabled: openRuns !== null,
  });

  const run = useMutation({
    mutationFn: (job: JobRow) => api.post(`/jobs/${job.name}/run`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.jobs() });
      toast.push({ tone: 'ok', message: 'Job started outside its schedule.' });
    },
    onError: (error) =>
      toast.push({
        tone: 'crit',
        message: error instanceof ApiProblem ? error.message : 'The job did not start.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      }),
  });

  const jobColumns: readonly Column<JobRow>[] = [
    { id: 'name', header: 'Job', priority: 1, mono: true, render: (job) => <span>{job.name}</span> },
    {
      id: 'state',
      header: 'State',
      priority: 1,
      render: (job) => (
        <Pill tone={!job.enabled ? 'neutral' : job.last_ok_at ? 'ok' : 'warn'}>
          {!job.enabled ? 'disabled' : job.last_ok_at ? 'ok' : 'never succeeded'}
        </Pill>
      ),
    },
    {
      id: 'last_run',
      header: 'Last run',
      priority: 1,
      render: (job) => <AgeText at={job.last_run_at} />,
    },
    {
      id: 'next_run',
      header: 'Next run',
      priority: 3,
      render: (job) => <AgeText at={job.next_run_at} />,
    },
    {
      id: 'description',
      header: 'What it does',
      priority: 4,
      render: (job) => <span>{job.description}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      priority: 2,
      align: 'right',
      render: (job) => (
        <span className="o-row">
          <Button size="sm" variant="ghost" onClick={() => setOpenRuns(job.name)}>
            Runs
          </Button>
          {can('jobs.run') ? (
            <Button size="sm" onClick={() => setRunNow(job)}>
              Run now
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <Card title="Health detail" id="health-detail">
        {health.isLoading ? <Skeleton shape="row" lines={4} label="Loading health" /> : null}
        {health.isError ? (
          <ErrorState message="Health detail did not load." onRetry={() => void health.refetch()} />
        ) : null}
        {(health.data?.checks ?? []).map((check) => (
          <div className="o-kv" id={check.check} key={check.check}>
            <span className="o-kv__key">
              {check.label} <span className="o-muted">{check.check}</span>
            </span>
            <span className="o-row">
              <Pill tone={check.state === 'ok' ? 'ok' : check.state === 'warn' ? 'warn' : check.state === 'crit' ? 'crit' : 'neutral'}>
                {check.state}
              </Pill>
              <span className="o-kv__value">{check.value ?? 'no value'}</span>
              <AgeText at={check.checked_at} />
            </span>
          </div>
        ))}
      </Card>

      <Card title="Build and dependencies">
        {system.isLoading ? <Skeleton shape="text" lines={4} label="Loading build" /> : null}
        {system.data ? (
          <>
            <div className="o-kv">
              <span className="o-kv__key">Version and build</span>
              <span className="o-kv__value">
                {system.data.version} · {system.data.build}
              </span>
            </div>
            <div className="o-kv">
              <span className="o-kv__key">Web bundle</span>
              <span className="o-kv__value">{WEB_BUILD}</span>
            </div>
            <div className="o-kv">
              <span className="o-kv__key">Uptime</span>
              <span className="o-kv__value">{Math.floor(system.data.uptime_sec / 3600)}h</span>
            </div>
            {(['db', 'wp', 'wc', 'bridge', 'jobs'] as const).map((key) => (
              <div className="o-kv" key={key}>
                <span className="o-kv__key">{key}</span>
                <span className="o-kv__value">{system.data?.[key]}</span>
              </div>
            ))}
            <p className="o-muted">
              All calendar math in this console is America/New_York. That is stated here once
              and nowhere else.
            </p>
          </>
        ) : null}
      </Card>

      <Card title="Jobs">
        {jobs.isError ? (
          <ErrorState message="The job table did not load." onRetry={() => void jobs.refetch()} />
        ) : (
          <DataTable<JobRow>
            caption="Scheduled jobs"
            columns={jobColumns}
            rows={jobs.data?.items ?? []}
            rowKey={(job) => job.name}
            loading={jobs.isLoading}
            emptyState={<EmptyState message="No jobs registered." />}
          />
        )}
      </Card>

      {openRuns ? (
        <Card
          title={`Recent runs: ${openRuns}`}
          action={
            <Button size="sm" variant="ghost" onClick={() => setOpenRuns(null)}>
              Close
            </Button>
          }
        >
          {runs.isLoading ? <Skeleton shape="row" lines={4} label="Loading runs" /> : null}
          {(runs.data?.items ?? []).map((entry) => (
            <div className="o-kv" key={entry.run_id}>
              <span className="o-kv__key">
                <AgeText at={entry.started_at} /> attempt {entry.attempt}
              </span>
              <span className="o-row">
                <Pill tone={entry.status === 'ok' ? 'ok' : entry.status === 'running' ? 'info' : 'crit'}>
                  {entry.status}
                </Pill>
                <span className="o-kv__value">
                  {entry.duration_ms === null ? 'running' : `${entry.duration_ms}ms`}
                  {entry.rows === null ? '' : ` · ${entry.rows} rows`}
                </span>
              </span>
            </div>
          ))}
          {!runs.isLoading && (runs.data?.items ?? []).length === 0 ? (
            <EmptyState message="No runs recorded yet." />
          ) : null}
        </Card>
      ) : null}

      <ConfirmDialog
        open={runNow !== null}
        title={runNow ? `Run ${runNow.name} now?` : ''}
        body="The job runs immediately, outside its schedule, alongside anything already running in its concurrency group."
        effects={[
          'One extra job run row',
          'Load on WooCommerce and the ops database',
          'An audit row naming you',
        ]}
        confirmLabel="Run it now"
        reasonRequired
        onCancel={() => setRunNow(null)}
        onConfirm={async () => {
          const job = runNow;
          setRunNow(null);
          if (job) await run.mutateAsync(job);
        }}
      />
    </>
  );
}
